import { useState } from "react";

import {
	Badge,
	Button,
	TableBody,
	TableCell,
	TableColumn,
	TableHeader,
	TableRow,
	Tooltip,
} from "@heroui/react";

import { Icon } from "@iconify/react/dist/iconify.js";
import {
	Chip,
	ErrorContent,
	OpenMode,
	OperationDrawer,
} from "@onesolution/components";
import type { OperationDetailsTabs } from "@onesolution/components/OperationDrawer/OperationDetails";
import Table, {
	baseTableBodyProps,
	baseTableRowProps,
} from "@onesolution/components/Table/Table";
import { StatusSelector } from "@onesolution/components/form/StatusSelector";
import TagsFilters from "@onesolution/components/form/TagsFilters";
import { type AppFormApi, useLanguage } from "@onesolution/hooks";
import type { TranslationsKeys } from "@onesolution/hooks/useLanguage";
import { useTagsFilters } from "@onesolution/hooks/useTagsFilters";
import { getTasksFromServer } from "@onesolution/serverFn";
import { markNotifAsReadOnServer } from "@onesolution/serverFn/user/markNotifAsRead";
import { formatFrontDate } from "@onesolution/utils";

export default function Tasks() {
	const { t } = useLanguage();
	const [drawerState, setDrawerState] = useState({
		mode: OpenMode.close,
		defaultTab: "overview" as OperationDetailsTabs,
		selectedOpId: "",
		onlyChat: false,
	});
	const { mutate: markAsRead } = markNotifAsReadOnServer.useMutation();
	const { form, filters } = useTagsFilters("tags-list");

	const {
		data,
		isFetched,
		isFetching,
		isError,
		refetch,
		fetchNextPage,
		hasNextPage,
	} = getTasksFromServer.useInfiniteQuery(filters);

	return (
		<div className="flex flex-col max-h-full gap-4">
			<div className="flex flex-row gap-2 items-center mb-6">
				<h1 className="text-3xl font-bold">{t("name.tasks")}</h1>
				<Chip
					isLoading={isFetching}
					value={data.items?.length}
					variant="flat"
				/>
			</div>

			{isError && <ErrorContent isFetching={isFetching} refetch={refetch} />}
			<TagsFilters form={form as AppFormApi} />

			<Table
				aria-label="your tasks"
				isFetching={isFetching}
				fetchNextPage={fetchNextPage}
				hasNextPage={hasNextPage}
			>
				<TableHeader>
					<TableColumn> </TableColumn>
					<TableColumn align="end"> </TableColumn>
					<TableColumn>{t("basic.deadline")}</TableColumn>
					<TableColumn>{t("name.type")}</TableColumn>
					<TableColumn>{t("name.product")}</TableColumn>
					<TableColumn>{t("name.operation")}</TableColumn>
					<TableColumn align="end"> </TableColumn>
				</TableHeader>
				<TableBody
					isLoading={!isFetched}
					items={data.items || []}
					{...baseTableBodyProps(t, form as AppFormApi)}
				>
					{(task) => (
						<TableRow key={task.id} {...baseTableRowProps()}>
							<TableCell className="min-w-52">
								<Tooltip content={task.name} delay={500}>
									<p className="line-clamp-2">{task.name}</p>
								</Tooltip>
							</TableCell>
							<TableCell className="min-w-52 flex justify-end items-center">
								{task.canEditStatus ? (
									<StatusSelector value={task.status.color} taskId={task.id} />
								) : (
									<Chip
										radius="sm"
										variant="dot"
										className="hover:shadow-none!"
										classNames={{
											dot: `bg-[${task.status.color}]`,
										}}
									>
										{t(`status.${task.status.label}` as TranslationsKeys) ||
											task.status.label}
									</Chip>
								)}
							</TableCell>
							<TableCell>
								{task.deadline && (
									<Chip radius="sm" variant="flat">
										{formatFrontDate(task.deadline)}
									</Chip>
								)}
							</TableCell>
							<TableCell>
								<Chip
									radius="sm"
									variant="flat"
									style={{ background: `${task.type.color}20` }}
								>
									{task.type.label}
								</Chip>
							</TableCell>
							<TableCell>
								<Chip radius="sm" variant="flat">
									{task.product}
								</Chip>
							</TableCell>
							<TableCell>
								{task.operations.map((operation) => (
									<Chip
										key={operation.id}
										onClick={() =>
											setDrawerState({
												...drawerState,
												mode: OpenMode.details,
												defaultTab: "overview",
												selectedOpId: operation.id,
												onlyChat: false,
											})
										}
										radius="sm"
										variant="flat"
										className="mr-2"
									>
										{operation.name}
									</Chip>
								))}
							</TableCell>
							<TableCell>
								<Button
									isIconOnly
									variant="light"
									onPress={async (e) => {
										await markAsRead({ pulseId: task.id });
										setDrawerState({
											mode: OpenMode.details,
											selectedOpId: task.id,
											defaultTab: "activity",
											onlyChat: true,
										});
									}}
								>
									<Badge
										color="danger"
										isDot
										content={" "}
										size="sm"
										hidden={!task.hasUnreadNotif}
									>
										<Icon icon="solar:chat-round-dots-line-duotone" />
									</Badge>
								</Button>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>

			<OperationDrawer
				mode={drawerState.mode}
				onClose={() =>
					setDrawerState({
						...drawerState,
						mode: OpenMode.close,
						selectedOpId: "",
					})
				}
				onlyChat={drawerState.onlyChat}
				defaultOpenedTab={drawerState.defaultTab}
				onEdit={() => {
					setDrawerState({
						...drawerState,
						mode: OpenMode.edition,
					});
				}}
				selectedOpId={drawerState.selectedOpId}
			/>
		</div>
	);
}
